import { Suspense, useEffect, useState } from 'react'
import { Button } from '@/components/shadcn/button'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { IconViewOutline } from '@icons/IconViewOutline'
import { IconRefreshOutline } from '@icons/IconRefreshOutline'
import { TooltipButton } from '@/components/ui/TooltipButton'
import { useRestClient } from '@/contexts/QueryProvider'
import { toast } from 'sonner'
import { isPlatformAdmin, isProjectAdmin, isProjectLeader, isProjectMember, isSuperUser } from '@/util'
import { useSessionStorage } from '@/hooks'

interface RevealKeyButtonProps {
  userId: number
}

const getAuthCodeFromResponse = (response: unknown): string | undefined => {
  if (!response || typeof response !== 'object') {
    return undefined
  }

  const authCodeValue = (response as { authCode?: unknown }).authCode
  return typeof authCodeValue === 'string' ? authCodeValue : undefined
}

interface AuthCodeFetcherProps {
  userId: number
  onResolved: (authCode: string) => void
  onFailed: () => void
}

function AuthCodeFetcher({ userId, onResolved, onFailed }: AuthCodeFetcherProps) {
  const { getExternalUserGetAuthCode } = useRestClient()
  const { data } = getExternalUserGetAuthCode.useSuspenseQuery(
    {
      path: { userId },
    },
    {
      staleTime: 0,
      refetchOnMount: 'always',
    }
  )

  useEffect(() => {
    const fetchedAuthCode = getAuthCodeFromResponse(data)
    if (fetchedAuthCode) {
      onResolved(fetchedAuthCode)
      return
    }

    onFailed()
  }, [data, onFailed, onResolved])

  return null
}

export function RevealKeyButton({ userId }: RevealKeyButtonProps) {
  const [authCode, setAuthCode] = useState<string | null>(null)
  const [isAuthCodeDialogOpen, setIsAuthCodeDialogOpen] = useState(false)
  const [isFetchingAuthCode, setIsFetchingAuthCode] = useState(false)
  const [fetchRequestId, setFetchRequestId] = useState(0)
  const [roles] = useSessionStorage('roles', 0)

  const allAccess = isSuperUser(roles) || isPlatformAdmin(roles)
  const canToggleApiAccess = isProjectLeader(roles) || isProjectAdmin(roles) || allAccess
  const canRotateAndViewKeys = canToggleApiAccess || isProjectMember(roles)

  const { postExternalUserChangePassword, getSupportApiGetPlatformUsers } = useRestClient()

  const { mutate: changeApiKey, isPending } = postExternalUserChangePassword.useMutation(undefined, {
    onSuccess: (response) => {
      const returnedAuthCode = getAuthCodeFromResponse(response)
      if (returnedAuthCode) {
        setAuthCode(returnedAuthCode)
        setIsAuthCodeDialogOpen(true)
      }
      toast.success('API key changed successfully.')
      getSupportApiGetPlatformUsers.invalidateQueries()
    },
    onError: () => {
      toast.error('Failed to change API key.')
    },
  })

  if (!canRotateAndViewKeys) {
    return <span className="text-muted-foreground">(Restricted)</span>
  }

  const onAuthCodeResolved = (fetchedAuthCode: string) => {
    setAuthCode(fetchedAuthCode)
    setIsAuthCodeDialogOpen(true)
    setIsFetchingAuthCode(false)
  }

  const onAuthCodeFailed = () => {
    toast.error('Failed to retrieve API key.')
    setIsFetchingAuthCode(false)
  }

  const onViewKey = () => {
    if (isFetchingAuthCode) {
      return
    }

    setIsFetchingAuthCode(true)
    setFetchRequestId((prev) => prev + 1)
  }

  return (
    <div className="flex items-center gap-2 pl-3">
      <TooltipButton tooltip="View Key">
        <Button
          onClick={onViewKey}
          variant="ghost"
          size="icon"
          disabled={isFetchingAuthCode}
          className="h-6 w-6 text-left hover:bg-transparent hover:!text-primary"
        >
          <IconViewOutline className="mr-0.5 inline-block size-4" />
        </Button>
      </TooltipButton>
      <TooltipButton tooltip="Rotate Key">
        <Button
          onClick={() => changeApiKey({ path: { userId } })}
          variant="ghost"
          size="icon"
          disabled={isPending}
          className="h-6 w-6 text-left hover:bg-transparent hover:!text-primary"
        >
          <IconRefreshOutline className="mr-0.5 inline-block size-4" />
        </Button>
      </TooltipButton>
      {isFetchingAuthCode && (
        <Suspense fallback={null}>
          <AuthCodeFetcher
            key={`${userId}-${fetchRequestId}`}
            userId={userId}
            onResolved={onAuthCodeResolved}
            onFailed={onAuthCodeFailed}
          />
        </Suspense>
      )}
      <ConfirmDialog
        isOpen={isAuthCodeDialogOpen}
        onOpenChange={setIsAuthCodeDialogOpen}
        title="API key"
        description={authCode ? `Auth code: ${authCode}` : undefined}
        confirmLabel="Close"
        cancelLabel="Close"
        onConfirm={() => setIsAuthCodeDialogOpen(false)}
        onCancel={() => setIsAuthCodeDialogOpen(false)}
      />
    </div>
  )
}
